import { Box, Chip, Typography } from "@mui/material";
import React, { useMemo } from "react";

import { EntityRef, ProductDetail } from "../../types/productPlane";
import CompareToggle from "../Compare/CompareToggle";
import { CountryFlag } from "../common/CountryFlag";
import EntityLink from "../Detail/EntityLink";
import { detailName, makerEntries } from "./productRelations";

interface ProductHeadProps {
  product: ProductDetail;
  // Drawn for a printed page: without the controls that act on the screen
  print?: boolean;
}

// The class chip is a link as a whole: the link fills the chip, so that a
// click anywhere on it opens the class
const classChipSx = {
  "& .MuiChip-label": { p: 0, alignSelf: "stretch", display: "flex" },
} as const;

const classLinkSx = {
  display: "flex",
  alignItems: "center",
  px: "11px",
} as const;

// A class above the product's own reads quieter than the class itself
const higherClassLinkSx = { ...classLinkSx, color: "text.secondary" } as const;

const ClassChip: React.FC<{ category: EntityRef; higher?: boolean }> = ({
  category,
  higher = false,
}) => (
  <Chip
    variant="outlined"
    sx={classChipSx}
    label={
      <EntityLink
        id={category.id}
        type={category.type}
        name={category.name}
        sx={higher ? higherClassLinkSx : classLinkSx}
      />
    }
  />
);

// The classes above the product's own on the category tree, from the root,
// each once: "Platforms", "Air" above "Fixed-wing UAV". A class the product
// is itself filed under is not repeated.
const higherClasses = (product: ProductDetail): EntityRef[] => {
  const seen = new Set(product.categories.map(({ category }) => category.id));
  const higher: EntityRef[] = [];
  for (const { path } of product.categories) {
    for (const above of path.slice(0, -1)) {
      if (!seen.has(above.id)) {
        seen.add(above.id);
        higher.push(above);
      }
    }
  }
  return higher;
};

// How many makers the head names before it counts the rest
const HEAD_MAKERS = 3;

// Who makes the product, under its name: "by Northgate Navigation ·
// Halden Dynamics (developer)". The makers a source attributes it to; only
// where there are none, the likely ones, said as such.
const MakerLine: React.FC<{ product: ProductDetail }> = ({ product }) => {
  const entries = useMemo(
    () => makerEntries(product.organizations),
    [product.organizations],
  );
  const attributed = entries.filter((entry) => !entry.likely);
  const shown = attributed.length > 0 ? attributed : entries;
  if (shown.length === 0) {
    return null;
  }
  const named = shown.slice(0, HEAD_MAKERS);
  const rest = shown.length - named.length;

  return (
    <Typography
      variant="body2"
      color="text.secondary"
      data-testid="product-head-makers"
    >
      {attributed.length > 0 ? "by " : "likely by "}
      {named.map((entry, index) => (
        <React.Fragment key={entry.organization.id}>
          {index > 0 && " · "}
          {entry.country && (
            <CountryFlag
              code={entry.country}
              size={12}
              round
              sx={{ mr: 0.5, verticalAlign: "-0.1em" }}
            />
          )}
          <EntityLink
            id={entry.organization.id}
            type={entry.organization.type}
            name={entry.organization.name}
            sx={{ fontWeight: 500 }}
          />
          {!entry.roles.includes("maker") && " (developer)"}
        </React.Fragment>
      ))}
      {rest > 0 && ` · +${rest} more`}
    </Typography>
  );
};

/**
 * The head of the product view: the name, its makers, its other names and
 * the chips that say what the entry is. How much evidence there is opens
 * the overview (see ProductEvidence).
 */
const ProductHead: React.FC<ProductHeadProps> = ({ product, print = false }) => {
  const higher = useMemo(() => higherClasses(product), [product]);

  // "Other names NG410, Northgate 410 · Original: Skäret"
  const otherNames = [
    product.other_names.length > 0
      ? `Other names ${product.other_names.join(", ")}`
      : "",
    product.original_name ? `Original: ${product.original_name}` : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box
      component="header"
      // A printed page has margins of its own
      sx={{
        px: print ? 0 : 3,
        pt: print ? 0 : 2.5,
        pb: print ? 0 : 1.75,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
      }}
    >
      {/* The head has no action area: the toggle ends the title row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            flex: 1,
            minWidth: 0,
            overflowWrap: "anywhere",
            color: product.name ? "text.primary" : "text.secondary",
            fontStyle: product.name ? "normal" : "italic",
          }}
        >
          {detailName(product)}
        </Typography>
        {!print && (
          <CompareToggle id={product.id} name={product.name} variant="button" />
        )}
      </Box>
      <MakerLine product={product} />
      {otherNames && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: "0.8125rem" }}
        >
          {otherNames}
        </Typography>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {higher.map((category) => (
          <ClassChip key={category.id} category={category} higher />
        ))}
        {product.categories.map(({ category }) => (
          <ClassChip key={category.id} category={category} />
        ))}
      </Box>
    </Box>
  );
};

export default ProductHead;
